import React from 'react';
import get from 'lodash/get';
import classNames from 'classnames';
import queryString from 'query-string';
import { InView } from 'react-intersection-observer';

import debounce from 'rebook/apps/utils/debounce';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import BookList from 'rebook/apps/books/components/BookList';
import Button from 'rebook/apps/base/components/Button';
import PreferencesSlider from 'rebook/apps/searchByPreferences/components/PreferencesSlider';
import CheckBox from 'rebook/apps/searchByPreferences/components/CheckBox';
import SpinnerLoader from 'rebook/apps/base/components/SpinnerLoader';
import EmptyBookList from 'rebook/apps/searchByPreferences/components/EmptyBookList';

import styles from './style.scss';


const debouncedFetch = debounce(fetchQuery, 2000);
const DEFAULT_SLIDER_VALUE = 50;

class SearchByPreferencesView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      slidersList: [],
      changedSliders: {},
      slidersAvailable: {},
      books: [],
      isLoadingBooks: false,
      showMobileButton: false,
      error: false,
    };
    this.bookListRef = React.createRef();
    this.onBlockVisible = this.onBlockVisible.bind(this);
  }

  componentDidMount() {
    this.fetchParameters();
  }

  fetchParameters() {
    const url = '/api/reco/book-selector/parameters/';
    fetchQuery(url)
      .then(({ data }) => {
        const slidersList = data.parameters;
        let changedSliders = {};
        for (let obj of slidersList) {
          const key = Object.keys(obj)[0];
          const slider = { ...obj[key], ...{ isChecked: true, value: DEFAULT_SLIDER_VALUE } };
          changedSliders = { ...changedSliders, [key]: slider };
        }
        window.scrollTo(0, 0);
        this.setState({ slidersList, changedSliders });
      })
      .catch(({ resp, data, error }) => {
        Raven.captureMessage('failed to load parameters for book selector', {
          level: 'error',
          extra: {
            status: resp.status,
            response: data,
            error,
          },
        });
        this.setState({ error: true });
      });
  }

  fetchRecommendedBooks(changedSliders) {
    let queryParams = {};
    for (let key in changedSliders) {
      const { isChecked, value } = changedSliders[key];
      if (isChecked) {
        queryParams[key] = value;
      }
    }
    const qs = queryString.stringify(queryParams);
    const url = `/api/reco/book-selector/?${qs}`;

    debouncedFetch(url)
      .then(({ data }) => {
        this.setState({ books: data, isLoadingBooks: false });
      })
      .catch(({ resp, data, error }) => {
        Raven.captureMessage('failed to load filtered books', {
          level: 'error',
          extra: {
            status: resp.status,
            response: data,
            error,
          },
        });
        this.setState({ error: true });
      });
  }

  handleChange(data) {
    const { changedSliders } = this.state;
    const { name, value } = data;
    if (!get(this, `state[${name}]`, null) || this.state[name] !== value) {
      const newChangedSliders = {
        ...changedSliders,
        [name]: { ...changedSliders[name], ...{ value } },
      };
      this.setState({
        changedSliders: newChangedSliders,
        isLoadingBooks: true,
        showMobileButton: true,
        books: [],
      });
      ga('send', 'event', {
        eventCategory: 'Recommend',
        eventAction: 'TouchControl',
        eventLabel: name,
      });

      this.fetchRecommendedBooks(newChangedSliders);
    }
  }

  handleCheckSlider(data) {
    const { changedSliders } = this.state;
    const { value, name } = data;
    const newChangedSliders = {
      ...changedSliders,
      [name]: { ...changedSliders[name], ...{ isChecked: value } },
    };
    this.setState({
      changedSliders: newChangedSliders,
      isLoadingBooks: true,
      showMobileButton: true,
      books: [],
    });
    ga('send', 'event', {
      eventCategory: 'Recommend',
      eventAction: value ? 'SwitchOn' : 'SwitchOff',
      eventLabel: name,
    });
    this.fetchRecommendedBooks(newChangedSliders);
  }

  getSliders(slidersList) {
    const { changedSliders } = this.state;
    let result = [];
    for (let item of slidersList) {
      const name = Object.keys(item)[0];
      const { from, to, min_val: minVal, max_val: maxVal } = item[name];
      const isChecked = changedSliders[name].isChecked;

      result.push(
        <div key={`slider-${name}-component`} className={styles.componentContainer}>
          <CheckBox
            name={name}
            isChecked={isChecked}
            extraClassName={styles.checkBox}
            onChange={(data) => {
              this.handleCheckSlider(data);
            }} />
          <PreferencesSlider
            key={`${name}-slider`}
            extraClassName={styles.sliderContainer}
            name={name}
            min={minVal}
            max={maxVal}
            minTickText={from}
            maxTickText={to}
            defaultValue={DEFAULT_SLIDER_VALUE}
            step={1}
            isDisabled={!isChecked}
            handleChange={(e) => {
              this.handleChange(e);
            }} />
        </div>,
      );
    }
    return result;
  }


  handleScroll() {
    const bookListTop = this.bookListRef.current.offsetTop + 84;
    window.scrollTo(0, bookListTop);
    this.setState({ showMobileButton: false });
  }

  onBlockVisible(isVisible) {
    this.setState({ showMobileButton: !isVisible });
  }

  render() {
    const listOfSliders = this.state && this.state.changedSliders;
    const sliderChecker = (list) => {
      for (let key in list) {
        if (list[key].isChecked) {
          return false;
        }
      }
      return true;
    };
    const allSlidersDisabled = sliderChecker(listOfSliders);

    const { slidersList, books, error, isLoadingBooks, showMobileButton } = this.state;
    return (
      <React.Fragment>
        <div className={classNames(styles.preferencesSearch)}>
          <div className={classNames(styles.preferencesSearchContainer, 'section')}>
            <div className={styles.preferencesSearchHeader}>
              <h1>
                Что бы вы хотели почитать?
              </h1>
              <span className={styles.preferencesSearchTitle}>
                Просто двигайте ползунки, чтобы подобрать интересные книги.
              </span>
            </div>
            {!error && (
              <div className={styles.preferencesSlidersContainer}>
                {this.getSliders(slidersList)}
              </div>
            )}
            {error && !allSlidersDisabled && (
              <SpinnerLoader />
            )}
          </div>
        </div>

        <div className="section" ref={this.bookListRef} id={'target'}>
          <div className={styles.bookList}>
            {(books && books.length > 0 && !isLoadingBooks && !allSlidersDisabled) && (
              <InView as="div" onChange={(isVisible) => this.onBlockVisible(isVisible)}>
                <BookList books={books} />
              </InView>
            )}

            {isLoadingBooks && !allSlidersDisabled && (
              <SpinnerLoader theme="premium" />
            )}

            {(allSlidersDisabled) && (
              <EmptyBookList />
            )}
            {(!isLoadingBooks && books.length === 0) && (
              <EmptyBookList noOneSelect />
            )}
          </div>
        </div>

        {!allSlidersDisabled && showMobileButton && (
          <div className={styles.buttonMobileBlock}>
            <Button
              handleClick={() => {
                this.handleScroll();
              }}
              extraClassName={styles.mobileButton}
              isInProgress={isLoadingBooks}
              size="small"
              theme="primary">
              Показать книги
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
};

export default SearchByPreferencesView;
